import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _regeneratorRuntime from "@babel/runtime/regenerator";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { Logger } from '../../../common/utils';
import { saveConsentPreferences } from '../../../services/consent-hub-service';
import { checkIfTokenIsDefault, StatusCodeError } from '../../../services/consent-hub-service/utils';
import { ContextualConsentProperty } from '../../../types';
import { setConsentTokenCookie, setConsentTokenFallbackCookie, setShouldShowBannerCookie } from '../set-required-consent-cookies';
import { packUserPreferences } from '../transformer';
import { mergeConsentUpdates, onlySetFallbackIfDifferent } from './utils';
export var createConsentTokenCookies = function createConsentTokenCookies(mergedPrefs, usingPrefetchedData) {
  // On a successful save, transform the prefs object into a formatted consentToken bit string
  // Then store the new token in a 10min-expiry consent cookie and a 1yr-expiry fallback cookie

  var consentToken = packUserPreferences(mergedPrefs);
  // If the token is prefetched and a default, we should rely on the existing cookie data rather than re-setting
  // cookies that are already set
  var tokenIsPrefetchedDefault = usingPrefetchedData && checkIfTokenIsDefault(consentToken);
  if (!tokenIsPrefetchedDefault) {
    if (usingPrefetchedData) {
      // If the fallback token already exists, we don't want to nuke the expiration inadvertently (since this happens on every page load).
      onlySetFallbackIfDifferent(consentToken);
    } else {
      // This has originated from a user saving their consents in the UI, so set a fallback to their new consents immediately.
      setConsentTokenFallbackCookie(consentToken);
    }
    setConsentTokenCookie(consentToken);
    setShouldShowBannerCookie(consentToken);
  }
};
export var createErrorDrivenTokens = function createErrorDrivenTokens(err, mergedPrefs) {
  var _objectSpread2;
  var errorDueToUnauthenticatedRequest = err instanceof StatusCodeError && err.status === 401;
  var errorDueToConsentHubBeingInaccessible = err instanceof StatusCodeError && err.status >= 500 && err.status < 600; // Any 5xx error

  // Indicates the user is genuinely logged out instead of ConsentHub being inaccessible
  // If we dont explicitly 401, defer to the previous auth state
  var updatedAuthState = errorDueToUnauthenticatedRequest ? !errorDueToUnauthenticatedRequest : !!mergedPrefs[ContextualConsentProperty.UserIsAuthenticated];
  var consentToken = packUserPreferences(_objectSpread(_objectSpread({}, mergedPrefs), {}, (_objectSpread2 = {}, _defineProperty(_objectSpread2, ContextualConsentProperty.UserIsAuthenticated, updatedAuthState), _defineProperty(_objectSpread2, ContextualConsentProperty.ConsentDataUnavailable, errorDueToConsentHubBeingInaccessible), _objectSpread2)));
  if (errorDueToUnauthenticatedRequest) {
    // When the failure is derived from a local-only/unauthenticated user, we need to also set the consent token
    // Otherwise, we will never actually adhere to their consents, as they'll continue to receive default every time we attempt to unsuccessfully fetch their prefs

    Logger.error("User is unauthenticated. ".concat(err.message));
    onlySetFallbackIfDifferent(consentToken);
    setConsentTokenCookie(consentToken);
    setShouldShowBannerCookie(consentToken);
    return;
  }

  // When there's an actual failure, we only store data in the fallbackConsentToken cookie
  // This allows us to still track consents when ConsentHub is down > 10 minute expiry
  // But do _not_ set a consentToken cookie so we attempt to re-request from ConsentHub on sequential loads
  // This circumvents an issue of a user thinking they saved consents to CH, but when we request them later,
  // CH has no record of them, and they get shown the banner again
  Logger.error('Token failed to save');
  onlySetFallbackIfDifferent(consentToken);
  setShouldShowBannerCookie(consentToken);
};

/**
  This logic only runs when a user initiates saving their consents **OR** when we've been provided prefetched data
  and we need to store the passed values, otherwise we'd have no way to set the associated cookies for the user

  @param updatedPreferences: This contains the user's current consents, which they are actively trying to update
  @param displayedTextMap: This contains a mapping of the text shown to the user in the UI for each checkbox
    used by ConsentHub for auditing compliance.
  @param usingPrefetchedData (Optional): This flag denotes this function execution has been triggered from prefetched data
    being passed into `initialPreferences` for the first time. We don't want to save this data to ConsentHub,
    as it's either already there or will never get saved (unauthed).
    It means we've been provided data to initialize our system's internal cookies with this pass.
 */
export var setConsentToken = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(updatedPreferences, displayedTextMap, usingPrefetchedData) {
    var mergedPrefs;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) switch (_context.prev = _context.next) {
        case 0:
          mergedPrefs = mergeConsentUpdates(updatedPreferences, usingPrefetchedData);
          _context.prev = 1;
          if (usingPrefetchedData) {
            _context.next = 5;
            break;
          }
          _context.next = 5;
          return saveConsentPreferences(mergedPrefs, displayedTextMap);
        case 5:
          createConsentTokenCookies(mergedPrefs, usingPrefetchedData);
          _context.next = 11;
          break;
        case 8:
          _context.prev = 8;
          _context.t0 = _context["catch"](1);
          // If we fail to save to ConsentHub, it could be due to ConsentHub being down, the user being unauthenticated/local-only,
          // or an exception somewhere in the stack. Ensure we pack that information into the token where applicable.
          createErrorDrivenTokens(_context.t0, mergedPrefs);
        case 11:
        case "end":
          return _context.stop();
      }
    }, _callee, null, [[1, 8]]);
  }));
  return function setConsentToken(_x, _x2, _x3) {
    return _ref.apply(this, arguments);
  };
}();